import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';

/**
* Angular injectable service responsible for managing appointment data and interacting with a backend API via ApiService. Provides methods to list, create, update, cancel, and fetch statistics for appointments.
* @component
* @selector appointment-service
* @example
*   <!-- Typical usage (in a component) -->
*   <!-- Inject via constructor: constructor(private appointmentService: AppointmentService) {} -->
*   <!-- Subscribe to results: this.appointmentService.getAppointments({date: '2026-03-17'}).subscribe(results => ...); -->
* @input any params - Generic parameter object used to filter or paginate appointment queries (e.g., { date: 'YYYY-MM-DD', status: 'pending', page: 1 }).
* @output Observable<any> result$ - Observables returned from service methods (e.g., getAppointments, getAppointmentById) that emit API responses; consumers should subscribe and handle success/error.
*
* Description:
*   AppointmentService is providedIn: 'root' and wraps ApiService calls to the appointment endpoints. It centralizes appointment-related API interactions so components and other services can request and manipulate appointment data without dealing directly with HTTP details.
*
* Constructor:
*   - constructor(apiService: ApiService)
*     Injects a generic ApiService used to perform HTTP GET/POST/PUT operations. ApiService handles lower-level request implementation and error propagation.
*
* Key methods:
*   - getAppointments(params: any): Observable<any>
*       Fetches a list of appointments using optional filter/pagination params.
*
*   - createAppointment(payload: any): Observable<any>
*       Creates a new appointment with the provided payload (POST /appointment).
*
*   - updateAppointment(appointmentId: string, payload: any): Observable<any>
*       Updates an existing appointment by ID (PUT /appointment/{id}).
*
*   - cancelAppointment(appointmentId: string, payload: any): Observable<any>
*       Cancels (or applies a cancellation update to) an appointment by ID (PUT /appointment/{id}).
*
*   - getTodaysAppointments(): Observable<any>
*       Convenience helper that queries appointments for today's date (uses ISO date yyyy-mm-dd).
*
*   - getCompletedAppointments(): Observable<any>
*       Retrieves appointments with status set to 'completed'.
*
*   - getPendingAppointments(): Observable<any>
*       Retrieves appointments with status set to 'pending'.
*
*   - getAppointmentStats(): Observable<any>
*       Fetches aggregated appointment statistics (e.g., counts for today, completed, pending).
*
*   - getAppointmentById(appointmentId: string): Observable<any>
*       Retrieves a single appointment by its ID.
*
* Important notes:
*   - All methods return Observable<any> and forward the ApiService responses; callers are responsible for subscription and error handling.
*   - Dates are formatted to ISO date strings where applicable (e.g., getTodaysAppointments).
*   - The service delegates authentication, headers, and common error handling to ApiService; it focuses purely on appointment endpoint semantics.
*   - Provided at root level (singleton) so state and caching strategies can be centralized if added later.
* Lifecycle hooks:
*   - As an injectable service, there are no Angular component lifecycle hooks; initialization is limited to construction and any explicit method calls.
*/
@Injectable({
  providedIn: 'root'
})
export class AppointmentService {

  constructor(private apiService: ApiService) { }

  getAppointments(params: any): Observable<any> {
    
    return this.apiService.get('appointment', params);
  }

  createAppointment(payload: any): Observable<any> {
    return this.apiService.post('appointment', payload);
  }

  updateAppointment(appointmentId: string, payload: any): Observable<any> {
    return this.apiService.put(`appointment/${appointmentId}`, payload);
  }

  // Get today's appointments count
  getTodaysAppointments(): Observable<any> {
    const [today] = new Date().toISOString().split('T');
    return this.apiService.get('appointment', { date: today });
  }

  // Get completed appointments count
  getCompletedAppointments(): Observable<any> {
    return this.apiService.get('appointment', { status: 'completed' });
  }

  // Get pending appointments count
  getPendingAppointments(): Observable<any> {
    return this.apiService.get('appointment', { status: 'pending' });
  }

  // Get appointment statistics (today's, completed, pending counts)
  getAppointmentStats(): Observable<any> {
    return this.apiService.get('appointment/statistics');
  }

  // Cancel appointment
  cancelAppointment(appointmentId: string, payload: any): Observable<any> {
    return this.apiService.put(`appointment/${appointmentId}`, payload);
  }

  // Get appointment by ID
  getAppointmentById(appointmentId: string): Observable<any> {
    return this.apiService.get(`appointment/${appointmentId}`);
  }
}